import React, { useEffect, useState } from 'react';
import { catalogosApi } from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, Lock, Tags, Layers3, Factory } from 'lucide-react';

const CATALOGOS = [
  { tipo: 'departamentos_contacto', label: 'Departamentos', icon: Layers3, descripcion: 'Áreas internas a las que pertenece un contacto (Técnico, Comercial, Compras…). Se usan en el organigrama de cada cliente.' },
  { tipo: 'funciones_contacto',     label: 'Funciones / Cargos', icon: Tags, descripcion: 'Roles profesionales que puede desempeñar un contacto (Ingeniero Jefe, Gerente, Comprador…).' },
  { tipo: 'materiales_fabricados',  label: 'Materiales que fabrica', icon: Factory, descripcion: 'Tipos de materiales que pueden fabricar las empresas marcadas como "fabricante" (Tuberías, Válvulas, Contadores…).' },
];

const Catalogos = () => {
  const [active, setActive] = useState(CATALOGOS[0].tipo);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: '', valor: '', orden: 999 });
  const [err, setErr] = useState('');

  const current = CATALOGOS.find((c) => c.tipo === active);

  const cargar = async () => {
    setLoading(true);
    try { setValues(await catalogosApi.list(active)); }
    catch (e) { console.error(e); setValues([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [active]);

  const abrirNuevo = () => {
    setEditing(null);
    setForm({ label: '', valor: '', orden: 999 });
    setErr('');
    setDialogOpen(true);
  };
  const abrirEditar = (v) => {
    setEditing(v);
    setForm({ label: v.label, valor: v.valor, orden: v.orden ?? 999 });
    setErr('');
    setDialogOpen(true);
  };
  const guardar = async (ev) => {
    ev.preventDefault();
    setErr('');
    if (!form.label.trim()) { setErr('El label es obligatorio'); return; }
    try {
      if (editing) {
        await catalogosApi.update(active, editing.valor, { label: form.label, orden: Number(form.orden) || 999 });
      } else {
        await catalogosApi.create(active, { label: form.label, valor: form.valor || undefined, orden: Number(form.orden) || 999 });
      }
      setDialogOpen(false);
      cargar();
    } catch (e) { setErr(e.message); }
  };
  const borrar = async (v) => {
    if (v.is_system) { alert('Este valor está protegido (is_system).'); return; }
    if (!window.confirm(`¿Eliminar "${v.label}"?`)) return;
    try { await catalogosApi.delete(active, v.valor); cargar(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div data-testid="catalogos-page">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-ink-900">Organigrama</h1>
        <p className="text-ink-500 mt-1">
          Configura los <span className="font-semibold">departamentos</span> y <span className="font-semibold">funciones</span> que alimentan el organigrama de cada cliente.
          Los valores marcados como <span className="inline-flex items-center gap-1 font-medium text-ink-700"><Lock className="w-3 h-3" /> del sistema</span> no se pueden borrar, pero sí renombrar.
        </p>
      </div>

      {/* Tipo tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="catalogo-tipo-tabs">
        {CATALOGOS.map(({ tipo, label, icon: Icon }) => (
          <button
            key={tipo}
            onClick={() => setActive(tipo)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-all ${
              active === tipo
                ? 'bg-brand-50 border-brand-200 text-brand-700'
                : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'
            }`}
            data-testid={`catalogo-tab-${tipo}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <Card className="p-5 border-ink-200 bg-white">
        <div className="flex items-start justify-between gap-4 mb-5">
          <p className="text-sm text-ink-500">{current?.descripcion}</p>
          <Button className="bg-brand-500 hover:bg-brand-600 text-white" onClick={abrirNuevo} data-testid="btn-nuevo-catalogo">
            <Plus className="w-4 h-4 mr-1.5" /> Nuevo valor
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-ink-400 text-center py-8">Cargando…</p>
        ) : values.length === 0 ? (
          <p className="text-sm text-ink-400 text-center py-8">Sin valores en este catálogo. Añade el primero.</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-200 text-ink-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left py-2 px-2 font-semibold">Label</th>
                <th className="text-left py-2 px-2 font-semibold">Valor (key)</th>
                <th className="text-left py-2 px-2 font-semibold w-20">Orden</th>
                <th className="text-right py-2 px-2 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100" data-testid="catalogo-tbody">
              {values.map((v) => (
                <tr key={v.valor} className="hover:bg-ink-50" data-testid={`catalogo-row-${v.valor}`}>
                  <td className="py-2 px-2 text-ink-900 font-medium">
                    {v.label}
                    {v.is_system && (
                      <Badge className="ml-2 text-[10px] bg-ink-100 border border-ink-200 text-ink-600">
                        <Lock className="w-3 h-3 mr-1" /> sistema
                      </Badge>
                    )}
                  </td>
                  <td className="py-2 px-2 text-ink-500 font-mono text-xs">{v.valor}</td>
                  <td className="py-2 px-2 text-ink-600">{v.orden}</td>
                  <td className="py-2 px-2 text-right">
                    <button onClick={() => abrirEditar(v)} className="p-1.5 rounded hover:bg-ink-100 text-ink-600" title="Editar">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => borrar(v)}
                      disabled={v.is_system}
                      className={`p-1.5 rounded text-red-600 ml-1 ${v.is_system ? 'opacity-30 cursor-not-allowed' : 'hover:bg-red-50'}`}
                      title={v.is_system ? 'Protegido' : 'Eliminar'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) setDialogOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="dialog-catalogo">
          <form onSubmit={guardar}>
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar valor' : 'Nuevo valor'}</DialogTitle>
              <DialogDescription>
                Catálogo: <span className="font-semibold">{current?.label}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Label *</label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ej: Ingeniero Jefe" autoFocus data-testid="cat-label" />
              </div>
              {!editing && (
                <div>
                  <label className="text-xs font-medium text-ink-600 mb-1 block">Valor (opcional)</label>
                  <Input value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} placeholder="Se genera desde el label si se omite" data-testid="cat-valor" />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Orden</label>
                <Input type="number" value={form.orden} onChange={(e) => setForm({ ...form, orden: e.target.value })} />
              </div>
              {err && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-guardar-catalogo">
                {editing ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Catalogos;
